// @flow
import { handleActions } from "redux-actions";
import {
    jsToUserSettingsRecord,
    UserSettings
} from "../records/userSettings.record";
import Record from "../flow/Record";
import {
    userProfileInit,
    userProfileInitFailure,
    userProfileInitSuccess,
    userCorrections,
    userCorrectionsSuccess,
    userCorrectionsFailure,
    userPosts as userPostsAction,
    userPostsSuccess,
    userPostsFailure,
    resetUserProfile
} from "../actions/userProfile.actions";
import { PagedRequest, RequestState, Edge } from "../records/request.record";
import {
    setupCorrectionPagedRequest,
    setupPostPagedRequest,
    setupRequest
} from "./request.reducer";
import { saveProfileSuccess } from "../actions/editProfile.actions";
import {
    deleteCorrectionSuccess,
    reportCorrectionSuccess,
    submitCommentSuccess,
    submitCorrectionSuccess,
    toggleCorrectionReactionSuccess
} from "../actions/correction.actions";
import {
    deletePostSuccess,
    submitPostSuccess,
    togglePostReactionSuccess
} from "../actions/post.actions";

const userRequest = setupRequest("userRequest");

export class UserProfile extends Record({
    userSettings: new UserSettings(),
    userRequest: new RequestState(),
    correctionsRequest: new PagedRequest(),
    postsRequest: new PagedRequest()
}) {
    userSettings: UserSettings;
    userRequest: RequestState;
    correctionsRequest: PagedRequest;
    postsRequest: PagedRequest;
}

const correctionsRequest = setupCorrectionPagedRequest("correctionsRequest");
const postsRequest = setupPostPagedRequest("postsRequest");

const updateUser = (obj, userSettings) => {
    const author = obj.author;
    if (userSettings.id === author.id) {
        return obj.set("author", obj.author.merge(userSettings));
    }
    return obj;
};

const updatePost = (post, userSettings) => {
    return updateUser(post, userSettings).merge({
        corrections: post.corrections.map(c => updateUser(c, userSettings))
    });
};

const updatePagedRequest = (request, userSettings, updateFunc) => {
    return request.set(
        "edges",
        request.edges.map(v => {
            return v.set("node", updateFunc(v.node, userSettings));
        })
    );
};

const updatePostsAndCorrections = (state, action) => {
    return correctionsRequest.updateCorrection(
        postsRequest.updateCorrection(state, action),
        action
    );
};

const updatePostsAndAppendCorrections = (state, action) => {
    state = postsRequest.updateCorrection(state, action);
    return correctionsRequest.unshiftCorrection(state, action);
};

function deleteCorrection(state, action) {
    return correctionsRequest.filter(
        postsRequest.deleteCorrection(state, action),
        c => c.id !== action.payload.correction.id
    );
}

const userPosts = handleActions(
    {
        [userProfileInit]: userRequest.start,
        [userProfileInitFailure]: userRequest.failure,
        [userProfileInitSuccess](state, { payload: { user } }) {
            state = userRequest.success(state);
            return state.merge({ userSettings: jsToUserSettingsRecord(user) });
        },
        [userCorrections]: correctionsRequest.start,
        [userCorrectionsSuccess]: correctionsRequest.success,
        [userCorrectionsFailure]: correctionsRequest.failure,
        [userPostsAction]: postsRequest.start,
        [userPostsSuccess]: postsRequest.success,
        [userPostsFailure]: postsRequest.failure,
        [saveProfileSuccess](state, { payload }) {
            return state.merge({
                userSettings: state.userSettings.merge(payload),
                correctionsRequest: updatePagedRequest(
                    state.correctionsRequest,
                    payload,
                    updateUser
                ),
                postsRequest: updatePagedRequest(
                    state.postsRequest,
                    payload,
                    updatePost
                )
            });
        },
        [toggleCorrectionReactionSuccess]: updatePostsAndCorrections,
        [submitCommentSuccess]: updatePostsAndAppendCorrections,
        [submitCorrectionSuccess]: updatePostsAndAppendCorrections,
        [togglePostReactionSuccess]: postsRequest.updatePost,
        [resetUserProfile]() {
            return new UserProfile();
        },
        [submitPostSuccess](state, { payload: { post } }) {
            if (state.userSettings.id === post.author.id) {
                let hasPost = false;
                state.postsRequest.edges.forEach(edge => {
                    if (edge.node.id === post.id) {
                        hasPost = true;
                        return false;
                    }
                });
                if (hasPost) {
                    return postsRequest.replace(state, post);
                }
                return state.set(
                    "postsRequest",
                    state.postsRequest.set(
                        "edges",
                        state.postsRequest.edges.unshift(
                            new Edge({
                                node: post,
                                cursor: post.lastUpdateDate
                            })
                        )
                    )
                );
            }
            return state;
        },
        [deletePostSuccess]: postsRequest.deletePost,
        [deleteCorrectionSuccess]: deleteCorrection,
        [reportCorrectionSuccess]: deleteCorrection
    },
    new UserProfile()
);

export default userPosts;



// WEBPACK FOOTER //
// ./src/reducers/userProfile.reducer.js